import type { FC, ChangeEvent, InputHTMLAttributes } from "react"

export interface TailwindTextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, `onChange`> {
  /** Label text for the input */
  label: string
  /** ID for the input (also used for label's htmlFor) */
  id?: string
  /** Name attribute for the input */
  name?: string
  /** Input type (text, email, password, etc.) */
  type?: string
  /** Placeholder text */
  placeholder?: string
  /** Current input value (for controlled inputs) */
  value?: string
  /** Whether the input is required */
  required?: boolean
  /** Whether the input is disabled */
  disabled?: boolean
  /** Error message to display */
  error?: string
  /** Helper text to display below the input */
  helperText?: string
  /** Callback for when input value changes */
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void
  /** Additional class name for the input element */
  inputClassName?: string
  /** Additional class name for the label element */
  labelClassName?: string
}

const TailwindTextInput: FC<TailwindTextInputProps> = ({
  label,
  id,
  name,
  type = `text`,
  placeholder = ``,
  value,
  required = false,
  disabled = false,
  error,
  helperText,
  onChange,
  inputClassName = ``,
  labelClassName = ``,
  ...restProps
}) => {
  // Generate an ID if none provided
  const inputId =
    id || `input-${name || Math.random().toString(36).substring(2, 9)}`

  // Handle input change
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.value, event)
    }
  }

  return (
    <div>
      <label
        htmlFor={inputId}
        className={`block text-sm/6 font-medium text-gray-900 ${required ? `after:ml-0.5 after:text-red-500 after:content-['*']` : ``} ${labelClassName}`}
      >
        {label}
      </label>
      <div className="mt-2">
        <input
          id={inputId}
          name={name || inputId}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          required={required}
          disabled={disabled}
          className={`block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline-1 -outline-offset-1 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:grid-cols-2 ${
            error
              ? `outline-red-500 focus:outline-red-500`
              : `outline-gray-300 focus:outline-indigo-600`
          } placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 ${inputClassName}`}
          {...restProps}
        />
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    </div>
  )
}

export default TailwindTextInput
